import React, { useEffect, useRef } from "react"

import { FEATURE_FLAGS } from "@/feature-flags"
import {
  getCompareByDisplayLabel,
  getCompareByGroupKeys,
  groupMetricOutputsByCompareBy,
  type CompareBy,
} from "@/metrics/compare-by"
import { StringValueYesNoChip } from "@/metrics/metric-display"
import type { Annotation } from "@/types"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { useFeatureFlagEnabled } from "posthog-js/react"

import { logger } from "@/lib/logger"
import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"

import { AggregateOverviewString } from "../../components/aggregate/AggregateOverviewString"
import { BinaryCompareByPctChart } from "../../components/charts/BinaryCompareByPctChart"
import { SingleBinary } from "../../components/single/SingleBinary"
import { CategoricalMetricRecord } from "../base/CategoricalMetricRecord"
import { ExpectedBodyInput } from "../components/ExpectedBodyInput"
import { HighlightedJson } from "../components/HighlightedJson"
import { MatchPathInput } from "../components/MatchPathInput"
import { CategoricalHumanFeedbackButton } from "../helpers/CategoricalHumanFeedbackButton"
import { parseResultJson } from "../helpers/parseResultJson"

const log = logger("MatchExpectedSimWrapperMetric")

// Match result enum values from backend
const MATCH_RESULTS = {
  MATCH: "MATCH", // found in observation + matched
  DIFF: "DIFF", // found in observation + not matched
  NOT_FOUND: "NOT_FOUND", // match path not found in observation
} as const

type MatchResult = (typeof MATCH_RESULTS)[keyof typeof MATCH_RESULTS]

const VALID_MATCH_RESULTS: MatchResult[] = [
  MATCH_RESULTS.MATCH,
  MATCH_RESULTS.DIFF,
  MATCH_RESULTS.NOT_FOUND,
]

const MATCH_RESULT_LABELS = {
  yes: "Match",
  no: "Diff",
  unknown: "Not Found",
} as const

/**
 * Map match_result string_value to the YES/NO/UNKNOWN shape that the shared
 * binary chip components understand, so we can reuse them with custom labels.
 */
function toBinaryMo<
  T extends { string_value?: string | null; float_value?: number | null },
>(mo: T): T {
  const mapped =
    mo.string_value === MATCH_RESULTS.MATCH
      ? "YES"
      : mo.string_value === MATCH_RESULTS.DIFF
        ? "NO"
        : mo.string_value === MATCH_RESULTS.NOT_FOUND
          ? "UNKNOWN"
          : mo.string_value
  return { ...mo, string_value: mapped }
}

function parseMatchResult(mo: RunMetricOutput): MatchResult | undefined {
  // Prefer string_value (always set by backend); fall back to result/result_json for old data
  if (mo.string_value != null && typeof mo.string_value === "string") {
    const s = mo.string_value
    if (VALID_MATCH_RESULTS.includes(s as MatchResult)) {
      return s as MatchResult
    }
  }
  const result = mo.result as { match_result?: string } | null | undefined
  if (
    result?.match_result &&
    VALID_MATCH_RESULTS.includes(result.match_result as MatchResult)
  ) {
    return result.match_result as MatchResult
  }
  if (mo.result_json && typeof mo.result_json === "object") {
    const explanation = parseResultJson<MatchExpectedSimWrapperExplanation>(
      mo.result_json,
    )
    if (
      explanation?.match_result &&
      VALID_MATCH_RESULTS.includes(explanation.match_result)
    ) {
      return explanation.match_result
    }
  }
  if (typeof mo.float_value === "number") {
    return mo.float_value === 1 ? MATCH_RESULTS.MATCH : MATCH_RESULTS.DIFF
  }
  return undefined
}

/**
 * Extract display data from the structured `result` field (new) or fall back
 * to `result_json` (old data). Combines execution result data with config.
 */
function getMatchExpectedExplanation(
  metricOutput: {
    result?: Record<string, unknown> | null
    result_json?: Record<string, unknown> | null
  },
  metricMetadata: Record<string, unknown> | null | undefined,
): MatchExpectedSimWrapperExplanation | null {
  const result = metricOutput.result as
    | {
        match_result?: string
        actual_value?: unknown
        expected_value?: unknown
        extracted_value?: unknown
      }
    | undefined

  if (result?.match_result) {
    const observationName = metricMetadata?.observation_name
    const matchPath = metricMetadata?.match_path
    return {
      observation_name:
        typeof observationName === "string" ? observationName : "",
      observation_value: result.actual_value,
      match_path: typeof matchPath === "string" ? matchPath : undefined,
      extracted_value: result.extracted_value,
      expected_body: result.expected_value,
      match_result: result.match_result as MatchResult,
    }
  }

  return (
    parseResultJson<MatchExpectedSimWrapperExplanation>(
      metricOutput.result_json,
    ) ?? null
  )
}

function matchResultToNumeric(r: MatchResult): number {
  return r === MATCH_RESULTS.MATCH ? 100 : 0
}

interface MatchExpectedSimWrapperExplanation {
  observation_name: string
  observation_value: unknown
  match_path?: string
  extracted_value?: unknown
  expected_body: unknown
  match_result: MatchResult
}

interface MatchExpectedSimWrapperDeepDiveProps {
  explanation: MatchExpectedSimWrapperExplanation
  matchResult: MatchResult
}

const MatchExpectedSimWrapperDeepDive: React.FC<
  MatchExpectedSimWrapperDeepDiveProps
> = ({ explanation, matchResult }) => {
  const jsonContainerRef = useRef<HTMLPreElement>(null)

  const formatJson = (obj: unknown): string => {
    try {
      return JSON.stringify(obj, null, 2)
    } catch {
      return String(obj)
    }
  }

  const getResultDisplay = (result: MatchResult) => {
    switch (result) {
      case MATCH_RESULTS.MATCH:
        return {
          text: "Match",
          className: "text-green-600",
        }
      case MATCH_RESULTS.DIFF:
        return {
          text: "Diff",
          className: "text-red-600",
        }
      case MATCH_RESULTS.NOT_FOUND:
        return {
          text: "Not Found",
          className: "text-yellow-600",
        }
      default:
        return {
          text: "N/A",
          className: "text-coval-text-muted",
        }
    }
  }

  // Auto-scroll to highlighted section when component mounts
  useEffect(() => {
    if (explanation.match_path && explanation.extracted_value !== undefined) {
      const timeoutId = setTimeout(() => {
        const highlightedElement = document.getElementById("highlighted-match")
        const container = jsonContainerRef.current
        if (highlightedElement && container) {
          const containerRect = container.getBoundingClientRect()
          const elementRect = highlightedElement.getBoundingClientRect()

          const elementTopRelativeToContainer =
            elementRect.top - containerRect.top + container.scrollTop

          const containerHeight = container.clientHeight
          const targetScrollTop =
            elementTopRelativeToContainer - containerHeight / 2

          container.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: "smooth",
          })
        }
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [explanation.match_path, explanation.extracted_value])

  const resultDisplay = getResultDisplay(matchResult)

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Left side: All other info */}
      <div className="space-y-4">
        {/* Result at top */}
        <div>
          <p className={`text-lg font-semibold ${resultDisplay.className}`}>
            {resultDisplay.text}
          </p>
        </div>

        {/* Expected vs Real comparison */}
        <div>
          <div className="space-y-2">
            <div>
              <h3 className="font-semibold text-sm mb-1">Expected</h3>
              <pre className="bg-muted/30 p-3 rounded-lg text-xs overflow-x-auto max-h-48">
                {formatJson(explanation.expected_body)}
              </pre>
            </div>
            <div>
              <h3 className="font-semibold text-sm mb-1">Real</h3>
              <pre className="bg-muted/30 p-3 rounded-lg text-xs overflow-x-auto max-h-48">
                {explanation.match_path &&
                explanation.extracted_value !== undefined
                  ? formatJson(explanation.extracted_value)
                  : formatJson(explanation.observation_value)}
              </pre>
            </div>
          </div>
        </div>

        {/* Match path and observation name */}
        <div className="pt-4 border-t border-coval-border space-y-2">
          {explanation.match_path && (
            <div>
              <h3 className="font-semibold text-sm">Match Path</h3>
              <p className="font-mono text-sm break-all">
                {explanation.match_path}
              </p>
            </div>
          )}
          <div>
            <h3 className="font-semibold text-sm">Observation Name</h3>
            <p className="font-mono text-sm break-all">
              {explanation.observation_name}
            </p>
          </div>
        </div>
      </div>

      {/* Right side: Full observation JSON with highlighting */}
      <div>
        <h3 className="font-semibold text-sm mb-2">Full Observation</h3>
        <div className="border border-coval-border rounded-lg overflow-hidden bg-muted/30">
          <pre
            ref={jsonContainerRef}
            className="px-3 pt-2 pb-3 text-xs overflow-auto max-h-96 m-0"
          >
            {explanation.match_path &&
            explanation.extracted_value !== undefined ? (
              <HighlightedJson
                json={explanation.observation_value}
                matchPath={explanation.match_path}
                extractedValue={explanation.extracted_value}
              />
            ) : (
              formatJson(explanation.observation_value)
            )}
          </pre>
        </div>
      </div>
    </div>
  )
}

interface MatchExpectedSimWrapperEditorProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

const MatchExpectedSimWrapperEditor: React.FC<
  MatchExpectedSimWrapperEditorProps
> = ({ metricRecordData, onChange }) => {
  const observationName =
    metricRecordData.metric_metadata?.observation_name || ""
  const isCustomObservationsEnabled = useFeatureFlagEnabled(
    FEATURE_FLAGS.CUSTOM_WRAPPER_OBSERVATIONS,
  )

  // Right now, only coval users can see it :)
  const observationOptions = {
    "Api: Pre-Simulation": "SIMULATION_WRAPPER_API_PRE_SIMULATION",
    "Api: Post-Simulation": "SIMULATION_WRAPPER_API_POST_SIMULATION",
    ...(isCustomObservationsEnabled && {
      "Custom: Pre-Simulation": "SIMULATION_WRAPPER_CHIME_PRE_SIMULATION",
      "Custom: Post-Simulation": "SIMULATION_WRAPPER_CHIME_POST_SIMULATION",
    }),
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium">Observation Name</label>
        <Select
          value={observationName || undefined}
          onValueChange={(value) => {
            onChange({
              ...metricRecordData,
              metric_metadata: {
                ...metricRecordData.metric_metadata,
                observation_name: value,
              },
            })
          }}
          required
        >
          <SelectTrigger>
            <SelectValue placeholder="Select observation" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(observationOptions).map(([label, value]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-coval-text-muted">
          The name of the simulation observation to read from
        </p>
      </div>

      <ExpectedBodyInput
        value={metricRecordData.metric_metadata?.expected_body || ""}
        onChange={(value) => {
          onChange({
            ...metricRecordData,
            metric_metadata: {
              ...metricRecordData.metric_metadata,
              expected_body: value,
            },
          })
        }}
        isRequired
      />

      <MatchPathInput
        value={metricRecordData.metric_metadata?.match_path || ""}
        onChange={(value) => {
          onChange({
            ...metricRecordData,
            metric_metadata: {
              ...metricRecordData.metric_metadata,
              match_path: value,
            },
          })
        }}
        expectedBodyValue={metricRecordData.metric_metadata?.expected_body}
      />
    </div>
  )
}

const MATCH_EXPECTED_CATEGORIES = [
  MATCH_RESULTS.MATCH,
  MATCH_RESULTS.DIFF,
  MATCH_RESULTS.NOT_FOUND,
] as const

const MATCH_EXPECTED_CATEGORY_COLORS: Record<string, string> = {
  [MATCH_RESULTS.MATCH]:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  [MATCH_RESULTS.DIFF]:
    "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  [MATCH_RESULTS.NOT_FOUND]:
    "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
}

export class MatchExpectedSimWrapperMetricRecord extends CategoricalMetricRecord {
  overviewSize = "medium" as const
  aggregate_units = "%"
  hasCustomVotingUI = true

  getCategoricalOptions(): string[] {
    return [...MATCH_EXPECTED_CATEGORIES]
  }

  getCategoryColors(): Record<string, string> {
    return MATCH_EXPECTED_CATEGORY_COLORS
  }

  renderHumanFeedbackValueButton(
    annotation: Annotation | null,
    onValueChange: (
      floatValue: number | null,
      stringValue: string | null,
      setValue: string[] | null,
    ) => void,
    onExplanationChange: (explanation: string | null) => void,
    otherUserAnnotations?: Annotation[],
  ): React.ReactNode {
    return (
      <CategoricalHumanFeedbackButton
        annotation={annotation}
        onValueChange={onValueChange}
        onExplanationChange={onExplanationChange}
        otherUserAnnotations={otherUserAnnotations}
        categories={[...MATCH_EXPECTED_CATEGORIES]}
        categoryColors={this.getCategoryColors()}
      />
    )
  }

  /**
   * Compare-by overview: % of time matched per dimension, excluding NOT_FOUND from the denominator.
   */
  getComparedOverviewNode(
    runRows: RunRowLike[],
    compareBy: CompareBy,
  ): React.ReactNode | null {
    const allGroupKeys = getCompareByGroupKeys(runRows, compareBy)
    if (!runRows.length || allGroupKeys.length === 0) {
      return null
    }
    const mo = this._getMetricOutputsFromRunRows(runRows)
    const groups = groupMetricOutputsByCompareBy(mo, runRows, compareBy)
    const data = allGroupKeys.map((key) => {
      const groupMo = groups.get(key) ?? []
      const evaluated = groupMo.filter((m) => {
        const r = parseMatchResult(m)
        return r === MATCH_RESULTS.MATCH || r === MATCH_RESULTS.DIFF
      })
      const matchCount = evaluated.filter(
        (m) => parseMatchResult(m) === MATCH_RESULTS.MATCH,
      ).length
      const pct =
        evaluated.length > 0 ? (matchCount / evaluated.length) * 100 : 0
      return {
        label: getCompareByDisplayLabel(key, compareBy),
        value: pct,
        color: compareBy.colors[key],
      }
    })
    return (
      <BinaryCompareByPctChart
        key={`${compareBy.dimension}-${compareBy.metadataKey ?? ""}`}
        data={data}
        valueLabel="% matched"
      />
    )
  }

  /**
   * Provide numeric values for grouped aggregation modes (avg / median / p95 / min / max).
   * For this match metric we treat a successful match as 100 and any other
   * non-unknown outcome as 0, so:
   * - Average gives the match rate in percent.
   * - P95 / median / min / max give 0% or 100% depending on distribution.
   */
  protected _getNumericValuesForAggregation(
    metricOutputs: RunMetricOutput[],
  ): number[] {
    return metricOutputs
      .map((mo) => {
        const matchResult = parseMatchResult(mo)
        if (matchResult === undefined) {
          return null
        }
        return matchResultToNumeric(matchResult)
      })
      .filter((v): v is number => v !== null)
  }

  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    return (
      <StringValueYesNoChip
        mo={toBinaryMo(mo)}
        metric={this}
        labels={MATCH_RESULT_LABELS}
      />
    )
  }

  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode | null {
    return (
      <SingleBinary
        mo={toBinaryMo(mo)}
        metric={this}
        labels={MATCH_RESULT_LABELS}
      />
    )
  }

  getOverviewNode(
    mo: RunMetricOutput[],
    controlledProps?: {
      includeUnknowns?: boolean
      onIncludeUnknownsChange?: (value: boolean) => void
      hideCheckbox?: boolean
    },
  ): React.ReactNode {
    const normalized = mo.map((m) => toBinaryMo(m))
    return (
      <AggregateOverviewString
        mos={normalized}
        isBinary
        binaryLabels={MATCH_RESULT_LABELS}
        targetCondition={this.metric_metadata?.target_condition}
        includeUnknowns={controlledProps?.includeUnknowns}
        onIncludeUnknownsChange={controlledProps?.onIncludeUnknownsChange}
        hideCheckbox={controlledProps?.hideCheckbox}
      />
    )
  }

  renderInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    context?: {
      runRows?: RunRowLike[]
      highlightValue?: number | string | null
      onBarClick?: (filter: { type: "exact"; value: string }) => void
      activeFilters?: ChartBarFilter[]
    },
  ): React.ReactNode {
    if (metricOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">
          No metric data available for this metric.
        </div>
      )
    }
    const normalized = metricOutputs.map((m) => toBinaryMo(m))
    return (
      <div className="w-full">
        <AggregateOverviewString
          mos={normalized}
          isBinary
          binaryLabels={MATCH_RESULT_LABELS}
          targetCondition={this.metric_metadata?.target_condition}
          onBarClick={context?.onBarClick}
          activeFilters={context?.activeFilters}
        />
      </div>
    )
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    try {
      const explanation = getMatchExpectedExplanation(
        mo,
        this.metric_metadata as Record<string, unknown> | undefined,
      )
      if (!explanation) {
        return (
          <div className="text-coval-text-muted">
            No detailed results available
          </div>
        )
      }
      const matchResult =
        parseMatchResult(mo) ??
        (mo.float_value === 1 ? MATCH_RESULTS.MATCH : MATCH_RESULTS.DIFF)

      return (
        <MatchExpectedSimWrapperDeepDive
          explanation={explanation}
          matchResult={matchResult}
        />
      )
    } catch (error) {
      log.error("Error parsing observation state metric explanation", error)
      return <div className="text-red-500">Error displaying metric details</div>
    }
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
  ): React.ReactNode {
    return (
      <MatchExpectedSimWrapperEditor
        metricRecordData={metricRecordData}
        onChange={onChange}
      />
    )
  }
}
